import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableHead, TableRow, Checkbox, Typography, Box,
} from '@mui/material';

/**
 * Sub-dialog "Antecedentes" da Etapa 8 — port do dialog de seleção real
 * (`AntecedenteProcesso` dentro de `AcaoEnviar.jsx`), a pedido do usuário
 * após a implementação inicial da etapa (que tinha deliberadamente ficado
 * sem seleção, replicando a decisão já tomada para a grid "Antecedentes
 * Ativos" da Etapa 4). Escopo revisado: esta seleção é local à ação
 * "Enviar" (`MudancaFaseEnvio.jsx`) — não reintroduz seleção na grid
 * somente-leitura (`TableAntecedentesAtivos.jsx`), que continua só consulta.
 *
 * Reaproveita o MESMO `antecedentesAtivos` já buscado para a grid — zero
 * fetch novo, mesma lista (já filtrada fail-closed contra fase Baixa pelo
 * container). Colunas reduzidas (Número/Responsável/Status) por ser um
 * dialog de seleção, não de consulta detalhada — a grid completa (8
 * colunas) já existe ao lado, no mesmo card.
 *
 * "Cancelar" limpa a seleção inteira (mesmo comportamento da tela real:
 * `onClick={() => { setOpenDialogAntecedentes(false); setAntecedentesSelecionados([]); }}`)
 * — diferente de um "Cancelar" comum que só fecha sem efeito colateral.
 * "Confirmar" só fecha, preservando o que foi marcado.
 *
 * `hideBackdrop` (default `false`) — só deve ser `true` quando este dialog é
 * usado ANINHADO dentro de outro `Dialog` já aberto (ex.: dentro de
 * `DialogAgendarCompromisso.jsx`, Etapa 9) para não empilhar 2 backdrops;
 * em `MudancaFaseEnvio.jsx` (Etapa 8), onde não há Dialog externo, deixar o
 * padrão `false` para manter o escurecimento normal.
 */
export default function DialogSelecionarAntecedentes({
  open, antecedentes, selecionados, onToggle, onSelecionarTodos, onClose, onCancelar, hideBackdrop,
  mensagem,
}) {
  const lista = antecedentes || [];
  const todosSelecionados = lista.length > 0 && lista.every(a => selecionados.includes(a.id));
  const algumSelecionado = selecionados.length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" hideBackdrop={hideBackdrop}>
      <DialogTitle>Antecedentes</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {mensagem}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={todosSelecionados}
                    indeterminate={!todosSelecionados && algumSelecionado}
                    onChange={event => onSelecionarTodos(event.target.checked)}
                    disabled={!lista.length}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Número</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Responsável</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map(antecedente => (
                <TableRow key={antecedente.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selecionados.includes(antecedente.id)}
                      onChange={() => onToggle(antecedente.id)}
                    />
                  </TableCell>
                  <TableCell>{antecedente.numero}</TableCell>
                  <TableCell>{antecedente.negociador}</TableCell>
                  <TableCell>{antecedente.fase}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!lista.length && (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
              Nenhum antecedente ativo encontrado.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancelar}>Cancelar</Button>
        <Button variant="contained" onClick={onClose}>Confirmar</Button>
      </DialogActions>
    </Dialog>
  );
}

DialogSelecionarAntecedentes.propTypes = {
  open: PropTypes.bool.isRequired,
  antecedentes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    numero: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    negociador: PropTypes.string,
    fase: PropTypes.string,
  })),
  selecionados: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSelecionarTodos: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancelar: PropTypes.func.isRequired,
  hideBackdrop: PropTypes.bool,
  mensagem: PropTypes.string,
};
DialogSelecionarAntecedentes.defaultProps = {
  antecedentes: null,
  hideBackdrop: false,
  mensagem: 'Selecione os antecedentes que também devem ser enviados para a nova fase:',
};
